import Vibrant from 'node-vibrant'
import type { Vec3 } from 'node-vibrant/lib/color'
import { hexToRgb } from 'node-vibrant/lib/util'
import { useEffect, useState } from 'react'
import { isPresent } from 'ts-extras'
import type { ArrayElement, Image, Product } from '~/types'
import { imageUrl, ImageUrlOptions, isMediaImage } from './shopify'

export function getYiq(rgb: Vec3): number {
	return (rgb[0] * 299 + rgb[1] * 587 + rgb[2] * 114) / 1000 / 255
}

export async function getDominantColor(image: HTMLImageElement) {
	try {
		const palette = await new Vibrant(image, { colorCount: 4 }).getPalette()
		const color = Object.values(palette)
			.filter(isPresent)
			.sort((a, b) => b.population - a.population)

		return color[0]
	} catch (error) {
		console.warn(`${getDominantColor.name}Error`, error)
		return null
	}
}

export function useDominantColor(src: string, options: ImageUrlOptions = { height: 100 }) {
	const [color, setColor] = useState<string | null>(null)
	useEffect(() => {
		const image = document.createElement('img')
		image.className = 'absolute hidden'
		image.crossOrigin = 'anonymous'
		image.src = imageUrl(src, options)
		image.addEventListener('load', calc)

		async function calc() {
			const result = await getDominantColor(image)
			setColor(result?.bodyTextColor === '#fff' ? 'text-gray-200' : 'text-gray-800')
		}
		document.body.appendChild(image)
		return () => {
			document.body.removeChild(image)
			image.removeEventListener('load', calc)
		}
	}, [src, options])
	return color
}

export function getVariantImages(variant: ArrayElement<Product['variants']>) {
	return [
		variant,
		variant.image_2?.reference,
		variant.image_3?.reference,
		variant.image_4?.reference,
		variant.image_5?.reference
	]
		.filter(isMediaImage)
		.map(img => img.image)
}

export function getShadowColor(hex: string) {
	const rgb = hexToRgb(hex)
	if (!rgb) return null
	const [r, g, b] = rgb
	const lightness = Math.floor((r + g + b) / 3)
	if (lightness > 200) return 'shadow-4-25'
	if (lightness > 60) return 'shadow-4-70'
	else return 'shadow-4-100'
}

export function getGalleryOverrides(product: Product, images: Product['images']): Product['images'] {
	const overrides = product.gallery_overrides?.reference
	if (!overrides) return images

	return images.map<Image>((image, index) => {
		const override = overrides[`image_${index + 1}` as 'image_1']?.reference
		if (!override || !('image' in override)) return image
		// Ignore placeholder images
		if (override.image?.url.includes('1x1')) return image
		return override.image ?? image
	})
}
